import React from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import numeral from 'numeral'

import { buildSchema, MIN_AMOUNT } from '../../validations/schemas'
import { TextField } from '../form/text-field'
import { AmountInput } from '../form/amount'

import styles from '../form/form.module.css'
import { ClabeField } from '../form/clabe-field'
import { SelectBank } from '../form/select-bank'
import { UploadFile } from '../form/upload-file'
import { Password } from '../form/password/password'
import { SubmitButton } from '../form/submit'
import { AccountSelector } from '../form/account-selector'
import { useCashoutsAllData } from '../../hooks/use-cashouts-all-data'
import { useSubmitCashout } from '../../hooks/use-submit-cashout'
import { getRequiredFields } from './utils'
import useUserContext from '../../../../hooks/useUserContext'

export const Credit = () => {
  const userContext = useUserContext()
  const user = userContext?.userData?.user

  const { cashoutAccounts, cashoutAvailable, banks } = useCashoutsAllData()

  const bankId = banks?.find(({ bank }) => bank.toLowerCase().includes(cashoutAccounts.lastPurchase.bank))?.bank_code

  const defaultCashoutAccountId = cashoutAccounts.defaultCashoutAccountId
  const maxAmount = cashoutAvailable?.available ?? 0

  const resolver = React.useCallback(
    async (values, context, options) => {
      const filedsRequired = getRequiredFields({
        defaultCashoutAccountId,
        selectedCashoutAccountId: values?.cashout_account_id,
      })
      const schema = buildSchema({ maxAmount, filedsRequired })
      return zodResolver(schema)(values, context, options)
    },
    [defaultCashoutAccountId, maxAmount]
  )

  const methods = useForm({
    resolver,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      amount: '',
      method: 'spei',
      name: user?.name,
      last_name: user?.last_name,
      bank_id: String(bankId),
      cashout_account_id: defaultCashoutAccountId
    },
  })

  const { submitCashout, successMessage } = useSubmitCashout({
    methods,
    user,
    banks,
    lastPurchaseBank: cashoutAccounts?.lastPurchase?.bank,
  })

  const getHelpTextAmount = () => {
    return (
      <p className={styles.helpText}>
        El monto mínimo es <strong>{numeral(MIN_AMOUNT).format('$0,000')}</strong> y el máximo es <strong>{numeral(maxAmount).format('$0,000')}</strong>
      </p>
    )
  }

  const showNewAccountFields = methods.watch('cashout_account_id') === `OTHER` || !defaultCashoutAccountId

  const rootError = methods.formState.errors.root

  return (
    <FormProvider {...methods}>

      <form onSubmit={methods.handleSubmit(submitCashout)} style={{ maxWidth: '500px', margin: '0 auto' }}>
        {successMessage && (
          <p className={styles.helpText} role="status" style={{ color: '#97ff48' }}>
            {successMessage}
          </p>
        )}

        <AmountInput
          required
          name="amount"
          label="Monto"
          helpText={getHelpTextAmount()}
          disabled={true}
        />

        <AccountSelector
          allowNewAccount
          name="cashout_account_id"
          label="Cuenta para retiro"
          required
        />

        {showNewAccountFields && (
          <>
            <div className={styles.fullnameRow}>
              <TextField
                required
                name="name"
                label="Nombre"
                placeholder="Nombre"
                disabled={true}
              />
              <TextField
                required
                name="last_name"
                label="Apellidos"
                placeholder="Apellidos"
                disabled={true}
              />
            </div>
            <div className={styles.helpTextRow}>
              <p className={styles.helpText}>Escribe tu nombre y apellidos tal cual esta en tu estado de cuenta de tu banco</p>
            </div>
            <SelectBank />

            <ClabeField
              required
              name="clabe"
              label="Cuenta CLABE de 18 dígitos"
              placeholder="123 456 7890123456789 0"
              disabled={true}
            />

            <div className={styles.fullnameRow}>

              <UploadFile
                name="bank_statement"
                label="Estado de cuenta bancario"
                helpText="PDF o imagen de tu estado de cuenta bancario donde se vea tu cuenta clabe y tu nombre."
                required
                accept={{
                  'application/pdf': ['.pdf'],
                  'image/jpeg': ['.jpg', '.jpeg'],
                  'image/png': ['.png'],
                }}
                specsLine="PDF o imagen (JPG, PNG) · máx. 10 MB"
              />

              <UploadFile
                name="csf"
                label="Constancia de situacion fiscal"
                helpText="PDF o imagen de tu constancia de situación fiscal"
                accept={{
                  'application/pdf': ['.pdf'],
                  'image/jpeg': ['.jpg', '.jpeg'],
                  'image/png': ['.png'],
                }}
                specsLine="PDF o imagen (JPG, PNG) · máx. 10 MB"
              />
            </div>
          </>
        )}

        <Password required name="password" label="Contraseña" placeholder="Contraseña" />

        {rootError?.message && (
          <p className={styles.generalError} role="alert">
            {rootError.message}
          </p>
        )}

        <SubmitButton />


      </form>
    </FormProvider>
  )
}